import React from 'react';
import toast from 'react-hot-toast';

import {
  messageReceivedFromExtensionSchema
} from '@poseidon/types/types/extensionMessage.type';

import type { RouterOutputs } from '@poseidon/api/types';
import type { ContextStateProps } from './extension.reducer';
import { initialValue, reducer } from './extension.reducer';

export type MessagesSendObj = RouterOutputs['messagingTypes']['sendTypes'];
export type MessageSendToExtension = {
  [K in keyof MessagesSendObj]: MessagesSendObj[K] extends never ? { type: K } : { type: K; data: MessagesSendObj[K] };
}[keyof MessagesSendObj];

type ContextActions = {
  postMessage: (data: MessageSendToExtension) => void;
};

type ContextProps = [Omit<ContextStateProps, 'observer' | 'reconnectAttempts' | 'port'>, ContextActions];

const initialActions: ContextActions = {
  postMessage: () => {
    throw new Error('post message not implemented');
  },
};

const ExtensionContext = React.createContext<ContextProps>([initialValue, initialActions]);

export const ExtensionProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = React.useReducer(reducer, initialValue);

  const {
    extensionId,
    extensionUpdatedVersionAvailable,
    observer,
    isRunning,
    running,
    port,
    reconnectAttempts,
    extensionConnectedStatus,
    runningStepId,
    runningCadenceId,
  } = state;

  React.useEffect(() => {
    const observer = new MutationObserver(([mutation]) => {
      const target = mutation?.target;
      if (target != null && target instanceof HTMLElement) {
        dispatch({ type: 'SET_EXTENSION_ID', id: target.getAttribute('ext-id') ?? undefined });
        dispatch({
          type: 'SET_EXTENSION_UPDATED_VERSION_AVAILABLE',
          version: target.getAttribute('ext-updated-version-available') ?? undefined,
        });
      }
    });

    dispatch({ type: 'SET_OBSERVER', observer });
  }, [dispatch]);

  React.useEffect(() => {
    const body = document.querySelector('body');
    if (!observer || body == null) return;

    dispatch({ type: 'SET_EXTENSION_ID', id: body.getAttribute('ext-id') ?? undefined });
    dispatch({
      type: 'SET_EXTENSION_UPDATED_VERSION_AVAILABLE',
      version: body.getAttribute('ext-updated-version-available') ?? undefined,
    });
    observer.observe(body, { attributes: true });
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [observer, dispatch]);

  const connectToExtension = React.useCallback(
    (extensionId: string) => {
      //this can be undefined because it comes from extension manifest. Don't remove `?`
      const port = chrome?.runtime?.connect?.(extensionId);
      port?.onMessage.addListener((message) => {
        const parsed = messageReceivedFromExtensionSchema.safeParse(message);
        if (!parsed.success) {
          return;
        }

        switch (parsed.data.type) {
          case 'CONNECT_AWK':
            return dispatch({ type: 'CONNECT_AWK' });
          case 'CHECK_MESSAGES': {
            return dispatch({ type: 'RUNNING', action: 'messages' });
          }
          case 'CHECK_SALES_NAV_MESSAGES': {
            return dispatch({ type: 'RUNNING', action: 'salesNavMessages' });
          }
          case 'CHECK_CONNECTIONS': {
            return dispatch({ type: 'RUNNING', action: 'connections' });
          }
          case 'CHECK_SENT_INVITATIONS': {
            return dispatch({ type: 'RUNNING', action: 'sentInvitations' });
          }
          case 'RUN_STEP_FINISHED': {
            return dispatch({ type: 'RUN_STEP_FINISHED' });
          }
          case 'RUN_CADENCE_FINISHED': {
            return dispatch({ type: 'RUN_CADENCE_FINISHED' });
          }
          case 'RUN_CADENCE': {
            return dispatch({
              type: 'RUN_CADENCE_START',
              id: parsed.data.data.id,
              explicitCadenceIds: parsed.data.data.explicitCadenceIds,
            });
          }
          case 'RUN_STEP': {
            return dispatch({ type: 'RUN_STEP_START', id: parsed.data.data.id });
          }
          case 'ERROR': {
            toast.error(parsed.data.data.message);
          }
        }
      });
      port?.onDisconnect.addListener(() =>
        setTimeout(() => {
          dispatch({ type: 'DISCONNECT' });
        }, 1000),
      );
      dispatch({ type: 'CONNECT', port });
    },
    [dispatch],
  );

  React.useEffect(() => {
    if (extensionId == null || extensionId === 'undefined') {
      return;
    }
    if (port == null && reconnectAttempts < 3) {
      connectToExtension(extensionId);
    }
  }, [extensionId, connectToExtension, port, reconnectAttempts]);

  //note that port does get reset once in a while
  const postMessage = React.useCallback((data: MessageSendToExtension) => port?.postMessage(data), [port]);

  const value: ContextProps = [
    {
      extensionConnectedStatus,
      isRunning,
      extensionId,
      extensionUpdatedVersionAvailable,
      running,
      runningStepId,
      runningCadenceId,
    },
    { postMessage },
  ];
  return <ExtensionContext.Provider value={value}>{children}</ExtensionContext.Provider>;
};

export const useExtensionContext = () => React.useContext(ExtensionContext);
